import { Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import ExerciseItem from "./ExerciseItem";
import SuperSetGroup from "./SuperSetGroup";
import AddExercisesDialog from "./AddExercisesDialog";
import AddSuperSetDialog from "./AddSuperSetDialog";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import TimerIcon from '@mui/icons-material/Timer';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import { groupExercisesForRendering, removeSuperSetLink } from "../../utils/exerciseUtils";
import { RTL } from "../../cache/CacheRtl";
import { SUPERSET_COLORS } from "../../utils/exerciseUtils";
import TranslationDialogButton from "../translation/TranslationDialogButton";

/**
 * field key -> Hebrew label for the translation panel. NEW_TEMPLATE_WORKOUT declares one `@TField`
 * (`name`); per FIELD, so another locale needs no entry here.
 */
const WORKOUT_FIELD_LABELS = { name: 'שם האימון' };

const WorkoutDayCard = React.memo(({
    dayNumber,
    dayExercises,
    numberOfDays,
    readOnly,
    onDeleteDay,
    onRemoveExerciseFromDay,
    onExerciseUpdate,
    onReorderExercises,
    onAddExercises,
    onAddSuperSet,
    onDuplicateDay,
    onPresentDay,
    workoutName,
    workoutNameHeb,
    /**
     * The saved NEW_TEMPLATE_WORKOUT id, or null for a day added but not yet saved. Only needed by
     * the translation panel — the rename itself is still buffered into the page's template state
     * and written by the page's own save button, keyed by day index.
     */
    workoutId,
    onWorkoutNameUpdate,
}) => {
    const isSuperSetEnabled = true;

    // Drag state for internal reorder
    const [draggedExerciseIndex, setDraggedExerciseIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [isInternalDrag, setIsInternalDrag] = useState(false);

    // 3-dot menu
    const [menuAnchor, setMenuAnchor] = useState(null);
    const isMenuOpen = Boolean(menuAnchor);

    // Add chooser dialog
    const [isAddChooserOpen, setIsAddChooserOpen] = useState(false);

    // Dialogs
    const [isAddExercisesOpen, setIsAddExercisesOpen] = useState(false);
    const [isAddCardioOpen, setIsAddCardioOpen] = useState(false);
    const [isAddCatalystOpen, setIsAddCatalystOpen] = useState(false);
    const [isAddSuperSetOpen, setIsAddSuperSetOpen] = useState(false);

    // Collapse/expand
    const [isExpanded, setIsExpanded] = useState(true);

    // Rename dialog. `tempName` holds the LEGACY English name and has no input any more — it is
    // carried so handleSaveRename can pass it through untouched (see there). English is edited in
    // the translation panel.
    const [isRenameOpen, setIsRenameOpen] = useState(false);
    const [tempName, setTempName] = useState(workoutName || '');
    const [tempNameHeb, setTempNameHeb] = useState(workoutNameHeb || '');
    // (handleRenameClick re-seeds both from props every time the dialog opens, so the pass-through
    // of `tempName` below can never write back an abandoned value.)

    // Internal drag handlers
    const handleExerciseDragStart = (e, groupStartIndex) => {
        setIsInternalDrag(true);
        setDraggedExerciseIndex(groupStartIndex);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", "internal-reorder");
    };

    const handleExerciseDragEnd = () => {
        setIsInternalDrag(false);
        setDraggedExerciseIndex(null);
        setDragOverIndex(null);
    };

    const handleExerciseDragOver = (e, targetIndex) => {
        if (!isInternalDrag) return;
        e.preventDefault();
        e.stopPropagation();
        if (draggedExerciseIndex !== null && draggedExerciseIndex !== targetIndex) {
            setDragOverIndex(targetIndex);
        }
    };

    const handleExerciseDragLeave = (e) => {
        if (!isInternalDrag) return;
        const rect = e.currentTarget.getBoundingClientRect();
        if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
            setDragOverIndex(null);
        }
    };

    const handleExerciseDrop = (e, targetIndex) => {
        if (!isInternalDrag) return;
        e.preventDefault();
        e.stopPropagation();
        if (draggedExerciseIndex !== null && draggedExerciseIndex !== targetIndex && onReorderExercises) {
            onReorderExercises(dayNumber, draggedExerciseIndex, targetIndex);
        }
        setIsInternalDrag(false);
        setDraggedExerciseIndex(null);
        setDragOverIndex(null);
    };

    // Exercise handlers
    const handleExerciseUpdate = (updatedExercise) => {
        if (onExerciseUpdate) {
            onExerciseUpdate(dayNumber, updatedExercise);
        }
    };

    const handleRemoveExercise = (exerciseId) => {
        if (onRemoveExerciseFromDay) {
            onRemoveExerciseFromDay(dayNumber, exerciseId);
        }
    };

    const handleUnlinkSuperSet = (exerciseA, exerciseB) => {
        if (onExerciseUpdate) {
            onExerciseUpdate(dayNumber, removeSuperSetLink(exerciseA));
            onExerciseUpdate(dayNumber, removeSuperSetLink(exerciseB));
        }
    };

    const handleRemoveSuperSetBoth = (exerciseA, exerciseB) => {
        if (onRemoveExerciseFromDay) {
            const idA = exerciseA.exerciseId || exerciseA.id;
            const idB = exerciseB.exerciseId || exerciseB.id;
            onRemoveExerciseFromDay(dayNumber, idA);
            // Small delay to avoid race condition with state updates
            setTimeout(() => onRemoveExerciseFromDay(dayNumber, idB), 0);
        }
    };

    // 3-dot menu handlers
    const handleMenuOpen = (e) => setMenuAnchor(e.currentTarget);
    const handleMenuClose = () => setMenuAnchor(null);

    const handleRenameClick = () => {
        handleMenuClose();
        setTempName(workoutName || '');
        setTempNameHeb(workoutNameHeb || '');
        setIsRenameOpen(true);
    };

    const handleDuplicateClick = () => {
        handleMenuClose();
        if (onDuplicateDay) onDuplicateDay(dayNumber);
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        onDeleteDay(dayNumber);
    };

    // Opens THIS workout on the studio board. Per-day and not a page-level action, because a board
    // shows one workout — the day whose card you opened the menu on is the one going on the screen.
    const handlePresentClick = () => {
        handleMenuClose();
        if (onPresentDay) onPresentDay(dayNumber);
    };

    // Rename dialog save
    const handleSaveRename = () => {
        if (onWorkoutNameUpdate) {
            const nameHeb = tempNameHeb.trim() || `אימון ${dayNumber + 1}`;
            onWorkoutNameUpdate(dayNumber, {
                // Pass the loaded English through; fall back to the Hebrew (then to the historical
                // default) only when there is nothing to pass through, i.e. a brand-new day.
                name: tempName.trim() || nameHeb || `Workout ${dayNumber + 1}`,
                nameHeb,
            });
        }
        setIsRenameOpen(false);
    };

    // Add chooser handlers
    const handleAddExercisesClick = () => {
        setIsAddChooserOpen(false);
        setIsAddExercisesOpen(true);
    };

    const handleAddCardioClick = () => {
        setIsAddChooserOpen(false);
        setIsAddCardioOpen(true);
    };

    const handleAddCatalystClick = () => {
        setIsAddChooserOpen(false);
        setIsAddCatalystOpen(true);
    };

    const handleAddSuperSetClick = () => {
        setIsAddChooserOpen(false);
        setIsAddSuperSetOpen(true);
    };

    const handleAddExercises = (exercises) => {
        if (onAddExercises) onAddExercises(dayNumber, exercises);
    };

    const handleAddSuperSet = (exerciseA, exerciseB) => {
        if (onAddSuperSet) onAddSuperSet(dayNumber, exerciseA, exerciseB);
    };

    // Group exercises for rendering
    const groups = groupExercisesForRendering(dayExercises);

    const getGroupKey = (group) => {
        if (group.type === 'superset') {
            return `ss-${group.exerciseA.templateExerciseId || group.exerciseA.exerciseId}-${group.exerciseB.templateExerciseId || group.exerciseB.exerciseId}`;
        }
        return group.exercise.templateExerciseId ||
            `${group.exercise.exerciseId || group.exercise.id}-${dayNumber}-${group.startIndex}`;
    };

    return (
        <Paper
            elevation={2}
            sx={{
                minWidth: 220,
                height: 'fit-content',
                alignSelf: 'start',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'white',
                borderRadius: 3,
                border: '1px solid #E2E8F0',
                overflow: 'hidden',
            }}
        >
            {/* Card Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                borderBottom: isExpanded ? '1px solid #F1F5F9' : 'none',
                backgroundColor: '#FAFBFC',
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': {
                    backgroundColor: '#F1F5F9',
                },
                transition: 'background-color 0.15s ease',
            }}
                onClick={(e) => {
                    if (e.target.closest('.MuiIconButton-root')) return;
                    setIsExpanded(prev => !prev);
                }}
            >
                {/* Exercise count badge + expand chevron (right side in RTL) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                        label={dayExercises.length}
                        size="small"
                        sx={{
                            backgroundColor: '#F1F5F9',
                            color: '#64748B',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 24,
                            minWidth: 24,
                        }}
                    />
                    <ExpandMoreIcon
                        sx={{
                            fontSize: 20,
                            color: '#94A3B8',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.25s ease',
                        }}
                    />
                </Box>

                {/* Workout name — the CANONICAL one only.
                    The English subtitle that used to sit under it rendered the legacy
                    `new_workout_template.name` column, which stops being the English source of
                    truth as soon as anyone edits English in the translation panel. A confidently
                    displayed stale name on every card is worse than no subtitle, and this is a
                    Hebrew-only dashboard — the English name was never for this reader. */}
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1E293B', lineHeight: 1.2 }}>
                        {workoutNameHeb || `אימון ${dayNumber + 1}`}
                    </Typography>
                </Box>

                {/* 3-dot menu (left side in RTL). A read-only program keeps the menu only for the
                    actions that CHANGE nothing — putting a system program on the studio screen is
                    one of them, and hiding the whole menu is what used to make it unreachable. */}
                {(!readOnly || onPresentDay) && (
                    <IconButton
                        onClick={handleMenuOpen}
                        size="small"
                        aria-label={`אפשרויות ${workoutNameHeb || `אימון ${dayNumber + 1}`}`}
                        sx={{ color: '#94A3B8', '&:hover': { color: '#64748B' } }}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                )}
                {readOnly && !onPresentDay && <Box sx={{ width: 28 }} />}
            </Box>

            {/* 3-dot Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={isMenuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: { borderRadius: 2, minWidth: 160, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
                }}
            >
                {onPresentDay && (
                    <MenuItem onClick={handlePresentClick} sx={{ fontSize: '0.875rem', direction: 'rtl' }}>
                        <ListItemIcon><SlideshowIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>הצג על מסך</ListItemText>
                    </MenuItem>
                )}
                {!readOnly && (
                    <MenuItem onClick={handleRenameClick} sx={{ fontSize: '0.875rem', direction: 'rtl' }}>
                        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>שנה שם</ListItemText>
                    </MenuItem>
                )}
                {!readOnly && (
                    <MenuItem onClick={handleDuplicateClick} sx={{ fontSize: '0.875rem', direction: 'rtl' }}>
                        <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>שכפל אימון</ListItemText>
                    </MenuItem>
                )}
                {!readOnly && numberOfDays > 1 && (
                    <MenuItem onClick={handleDeleteClick} sx={{ fontSize: '0.875rem', color: 'error.main', direction: 'rtl' }}>
                        <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
                        <ListItemText>מחק אימון</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Collapsible Body */}
            <Collapse in={isExpanded} timeout={250}>
            {/* Exercise List */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
                {groups.map((group) => {
                    if (group.type === 'superset') {
                        return (
                            <Box
                                key={getGroupKey(group)}
                                draggable={!readOnly}
                                onDragStart={(e) => !readOnly && handleExerciseDragStart(e, group.startIndex)}
                                onDragEnd={handleExerciseDragEnd}
                                onDragOver={(e) => handleExerciseDragOver(e, group.startIndex)}
                                onDragLeave={handleExerciseDragLeave}
                                onDrop={(e) => handleExerciseDrop(e, group.startIndex)}
                                sx={{
                                    position: 'relative',
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing' },
                                    opacity: draggedExerciseIndex === group.startIndex ? 0.5 : 1,
                                    transition: 'opacity 0.2s',
                                }}
                            >
                                {dragOverIndex === group.startIndex && draggedExerciseIndex !== group.startIndex && isInternalDrag && (
                                    <Box sx={{
                                        position: 'absolute', top: -2, left: 0, right: 0, height: 3,
                                        backgroundColor: SUPERSET_COLORS.accent, borderRadius: 1, zIndex: 10,
                                        boxShadow: `0 0 8px ${SUPERSET_COLORS.accent}40`
                                    }} />
                                )}
                                <SuperSetGroup
                                    exerciseA={group.exerciseA}
                                    exerciseB={group.exerciseB}
                                    readOnly={readOnly}
                                    onUnlink={() => handleUnlinkSuperSet(group.exerciseA, group.exerciseB)}
                                    onRemoveBoth={() => handleRemoveSuperSetBoth(group.exerciseA, group.exerciseB)}
                                    onExerciseUpdate={handleExerciseUpdate}
                                />
                            </Box>
                        );
                    }

                    // Single exercise
                    return (
                        <Box
                            key={getGroupKey(group)}
                            draggable
                            onDragStart={(e) => handleExerciseDragStart(e, group.startIndex)}
                            onDragEnd={handleExerciseDragEnd}
                            onDragOver={(e) => handleExerciseDragOver(e, group.startIndex)}
                            onDragLeave={handleExerciseDragLeave}
                            onDrop={(e) => handleExerciseDrop(e, group.startIndex)}
                            sx={{
                                position: 'relative',
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' },
                                opacity: draggedExerciseIndex === group.startIndex ? 0.5 : 1,
                                transition: 'opacity 0.2s',
                            }}
                        >
                            {dragOverIndex === group.startIndex && draggedExerciseIndex !== group.startIndex && isInternalDrag && (
                                <Box sx={{
                                    position: 'absolute', top: -2, left: 0, right: 0, height: 3,
                                    backgroundColor: 'primary.main', borderRadius: 1, zIndex: 10,
                                    boxShadow: '0 0 8px rgba(33, 150, 243, 0.6)'
                                }} />
                            )}
                            <ExerciseItem
                                exercise={group.exercise}
                                exerciseNumber={group.exercise.index + 1}
                                readOnly={readOnly}
                                onRemove={handleRemoveExercise}
                                onExerciseUpdate={handleExerciseUpdate}
                            />
                        </Box>
                    );
                })}

                {/* Empty state */}
                {dayExercises.length === 0 && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 4,
                        gap: 1,
                    }}>
                        <FitnessCenterIcon sx={{ color: '#CBD5E1', fontSize: 36 }} />
                        <Typography sx={{ color: '#94A3B8', fontSize: '0.85rem', fontStyle: 'italic' }}>
                            לחץ על + להוספת תרגילים
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* "+" Add Button - hidden when readOnly */}
            {!readOnly && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 2,
                    pt: 0,
                }}>
                    <RTL>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setIsAddChooserOpen(true)}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                color: '#57837E',
                                borderColor: '#57837E',
                                px: 3,
                                '&:hover': {
                                    backgroundColor: '#F0FAF8',
                                    borderColor: '#456b67',
                                }
                            }}
                        >
                            הוסף
                        </Button>
                    </RTL>
                </Box>
            )}

            </Collapse>

            {/* Add Chooser Dialog */}
            <Dialog
                open={isAddChooserOpen}
                onClose={() => setIsAddChooserOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '14px',
                        minWidth: { xs: '90%', sm: 640 },
                        maxWidth: 720,
                        p: 0,
                    }
                }}
            >
                <DialogContent sx={{ p: 3, direction: 'rtl' }}>
                    <Typography sx={{
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        color: '#1E293B',
                        textAlign: 'center',
                        mb: 3,
                    }}>
                        מה תרצה להוסיף?
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        {/* Add Exercises Option */}
                        <Box
                            onClick={handleAddExercisesClick}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 3,
                                borderRadius: '12px',
                                border: '2px solid #E2E8F0',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: '#57837E',
                                    backgroundColor: '#F0FAF8',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(87, 131, 126, 0.15)',
                                }
                            }}
                        >
                            <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                backgroundColor: '#F0FAF8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <FitnessCenterIcon sx={{ fontSize: 28, color: '#57837E' }} />
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1E293B' }}>
                                תרגילים
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', textAlign: 'center', lineHeight: 1.3 }}>
                                הוסף תרגיל אחד או יותר
                            </Typography>
                        </Box>

                        {/* Add Cardio Option */}
                        <Box
                            onClick={handleAddCardioClick}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 3,
                                borderRadius: '12px',
                                border: '2px solid #E2E8F0',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: '#E91E63',
                                    backgroundColor: '#FFF0F5',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(233, 30, 99, 0.15)',
                                }
                            }}
                        >
                            <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                backgroundColor: '#FFF0F5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <DirectionsRunIcon sx={{ fontSize: 28, color: '#E91E63' }} />
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1E293B' }}>
                                קרדיו
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', textAlign: 'center', lineHeight: 1.3 }}>
                                הוסף תרגיל קרדיו
                            </Typography>
                        </Box>

                        {/* Add Catalyst Option */}
                        <Box
                            onClick={handleAddCatalystClick}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 3,
                                borderRadius: '12px',
                                border: '2px solid #E2E8F0',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: '#FF6B00',
                                    backgroundColor: '#FFF8F0',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(255, 107, 0, 0.15)',
                                }
                            }}
                        >
                            <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                backgroundColor: '#FFF8F0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <TimerIcon sx={{ fontSize: 28, color: '#FF6B00' }} />
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1E293B' }}>
                                תרגיל מבוסס זמן
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', textAlign: 'center', lineHeight: 1.3 }}>
                                סטים מבוססי זמן
                            </Typography>
                        </Box>

                        {/* Add Super Set Option */}
                        <Box
                            onClick={isSuperSetEnabled ? handleAddSuperSetClick : undefined}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 3,
                                borderRadius: '12px',
                                border: '2px solid #E2E8F0',
                                ...(isSuperSetEnabled
                                    ? { cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { borderColor: SUPERSET_COLORS.accent, backgroundColor: '#FAF5FF', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(124, 58, 237, 0.15)' } }
                                    : { opacity: 0.5, cursor: 'default' }),
                            }}
                        >
                            <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                backgroundColor: isSuperSetEnabled ? '#FAF5FF' : '#F1F5F9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <SyncAltIcon sx={{ fontSize: 28, color: isSuperSetEnabled ? SUPERSET_COLORS.accent : '#CBD5E1' }} />
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: isSuperSetEnabled ? '#1E293B' : '#94A3B8' }}>
                                סופר סט
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: isSuperSetEnabled ? '#94A3B8' : '#CBD5E1', textAlign: 'center', lineHeight: 1.3 }}>
                                {isSuperSetEnabled ? 'הוסף זוג תרגילים כסופר סט' : 'בקרוב'}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Rename Dialog */}
            <Dialog
                open={isRenameOpen}
                onClose={() => setIsRenameOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '14px',
                        minWidth: { xs: '90%', sm: 380 },
                        maxWidth: 420,
                    }
                }}
            >
                <DialogTitle sx={{
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#1E293B',
                    textAlign: 'center',
                    pb: 1,
                    borderBottom: '1px solid #E2E8F0',
                }}>
                    שנה שם אימון
                </DialogTitle>
                <DialogContent sx={{ pt: 3, direction: 'rtl' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#64748B', mb: 0.5, display: 'block' }}>
                                שם בעברית
                            </Typography>
                            <TextField
                                value={tempNameHeb}
                                onChange={(e) => setTempNameHeb(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveRename()}
                                placeholder="אימון"
                                size="small"
                                fullWidth
                                autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                    '& .MuiInputBase-input': { textAlign: 'right' }
                                }}
                            />
                        </Box>
                        {/* Every other language, rendered from
                            GET /admin/translation/schema/NEW_TEMPLATE_WORKOUT. The Hebrew above is
                            the canonical name and is saved with the PAGE (the template's own save
                            button); translations save themselves immediately, which is also why a
                            day that has never been saved has no id to translate against and the
                            panel says so. */}
                        <Box>
                            <TranslationDialogButton
                                type="NEW_TEMPLATE_WORKOUT"
                                entityId={workoutId ?? null}
                                fieldLabels={WORKOUT_FIELD_LABELS}
                                entityNoun="האימון"
                                dialogTitle="תרגומי שם האימון"
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: '1px solid #E2E8F0', justifyContent: 'center', gap: 1 }}>
                    <Button
                        onClick={() => setIsRenameOpen(false)}
                        variant="outlined"
                        sx={{
                            color: '#64748B',
                            borderColor: '#E2E8F0',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' }
                        }}
                    >
                        ביטול
                    </Button>
                    <Button
                        onClick={handleSaveRename}
                        variant="contained"
                        sx={{
                            backgroundColor: '#57837E',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            '&:hover': { backgroundColor: '#456b67' }
                        }}
                    >
                        שמור
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialogs */}
            <AddExercisesDialog
                isOpen={isAddExercisesOpen}
                close={() => setIsAddExercisesOpen(false)}
                onAdd={handleAddExercises}
                excludeCardio
            />
            <AddExercisesDialog
                isOpen={isAddCardioOpen}
                close={() => setIsAddCardioOpen(false)}
                onAdd={handleAddExercises}
                title="הוסף תרגיל קרדיו"
                initialTargetMuscle={{ value: "CARDIO", name: "קרדיו" }}
                hideFilter
            />
            <AddExercisesDialog
                isOpen={isAddCatalystOpen}
                close={() => setIsAddCatalystOpen(false)}
                onAdd={handleAddExercises}
                title="הוסף תרגיל מבוסס זמן"
                catalystOnly
            />
            <AddSuperSetDialog
                isOpen={isAddSuperSetOpen}
                close={() => setIsAddSuperSetOpen(false)}
                onAdd={handleAddSuperSet}
            />
        </Paper>
    );
});

export default WorkoutDayCard;
